import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNativeFeatures } from '@/hooks/useNativeFeatures';
import { usePWA } from '@/hooks/usePWA';
import { useOfflineStorage } from '@/hooks/useOfflineStorage';
import { toast } from 'sonner';
import {
  Smartphone,
  Camera,
  MapPin,
  Bell,
  Wifi,
  WifiOff,
  Download,
  Battery,
  Signal,
  Globe,
  RotateCw as Sync,
  CloudOff
} from 'lucide-react';

export const EnhancedMobileFeatures = () => {
  const {
    isNative,
    permissions,
    takePicture,
    getCurrentLocation,
    showLocalNotification,
    initializeNativeFeatures
  } = useNativeFeatures();

  const {
    isInstallable,
    isInstalled,
    isOnline,
    installApp
  } = usePWA();

  const {
    isOnline: storageOnline,
    pendingActions,
    addOfflineAction,
    saveDataOffline,
    getDataOffline
  } = useOfflineStorage();

  useEffect(() => {
    if (isNative) {
      initializeNativeFeatures();
    }
  }, [isNative, initializeNativeFeatures]);

  const handleTakePicture = async () => {
    try {
      const imageData = await takePicture();
      if (imageData) {
        toast.success('Photo captured successfully!');
      }
    } catch (error) {
      toast.error('Failed to take picture: ' + (error as Error).message);
    }
  };

  const handleGetLocation = async () => {
    try {
      const location = await getCurrentLocation();
      if (location) {
        toast.success(`Location: ${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`);
      }
    } catch (error) {
      toast.error('Failed to get location: ' + (error as Error).message);
    }
  };

  const handleShowNotification = async () => {
    try {
      await showLocalNotification('Test Notification', 'This is a test notification from the mobile app');
      toast.success('Notification sent!');
    } catch (error) {
      toast.error('Failed to show notification: ' + (error as Error).message);
    }
  };

  const handleOfflineAction = async () => {
    await addOfflineAction({
      type: 'create',
      endpoint: '/api/test',
      data: { message: 'Test offline action', timestamp: Date.now() }
    });
    toast.success('Action queued for offline sync');
  };

  const handleSaveOfflineData = async () => {
    await saveDataOffline('test-data', {
      id: Date.now(),
      message: 'Test data saved offline',
      timestamp: new Date().toISOString()
    });
    toast.success('Data saved offline');
  };

  const handleInstallApp = () => {
    installApp();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Mobile Features</h2>
          <p className="text-muted-foreground">
            Native mobile capabilities, PWA features, and offline functionality
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant={isOnline ? 'default' : 'destructive'}>
            {isOnline ? <Wifi className="mr-1 h-3 w-3" /> : <WifiOff className="mr-1 h-3 w-3" />}
            {isOnline ? 'Online' : 'Offline'}
          </Badge>
          <Badge variant={isNative ? 'default' : 'secondary'}>
            <Smartphone className="mr-1 h-3 w-3" />
            {isNative ? 'Native App' : 'Web App'}
          </Badge>
        </div>
      </div>

      {/* PWA Install Prompt */}
      {isInstallable && !isInstalled && (
        <Card className="border-primary">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Download className="h-5 w-5 text-primary" />
                <div>
                  <h3 className="font-semibold">Install App</h3>
                  <p className="text-sm text-muted-foreground">
                    Install this app for better performance and offline access
                  </p>
                </div>
              </div>
              <Button onClick={handleInstallApp}>
                Install
              </Button>
            </div>
          </CardContent>
        </Card>
      )}


      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Platform</CardTitle>
            <Smartphone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{isNative ? 'Native' : 'Web'}</div>
            <p className="text-xs text-muted-foreground">
              {isNative ? 'Running as native app' : 'Running in browser'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Connection</CardTitle>
            {isOnline ? <Wifi className="h-4 w-4 text-green-500" /> : <WifiOff className="h-4 w-4 text-red-500" />}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{isOnline ? 'Online' : 'Offline'}</div>
            <p className="text-xs text-muted-foreground">
              Network connectivity status
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Camera</CardTitle>
            <Camera className={`h-4 w-4 ${permissions.camera ? 'text-green-500' : 'text-muted-foreground'}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{permissions.camera ? 'Enabled' : 'Disabled'}</div>
            <p className="text-xs text-muted-foreground">
              Camera permission status
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Location</CardTitle>
            <MapPin className={`h-4 w-4 ${permissions.location ? 'text-green-500' : 'text-muted-foreground'}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{permissions.location ? 'Enabled' : 'Disabled'}</div>
            <p className="text-xs text-muted-foreground">
              Location permission status
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="native" className="space-y-4">
        <TabsList>
          <TabsTrigger value="native">Native Features</TabsTrigger>
          <TabsTrigger value="pwa">PWA Features</TabsTrigger>
          <TabsTrigger value="offline">Offline Capabilities</TabsTrigger>
        </TabsList>

        <TabsContent value="native" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Device Features</CardTitle>
                <CardDescription>
                  Access native device capabilities
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  onClick={handleTakePicture} 
                  disabled={!permissions.camera}
                  className="w-full"
                >
                  <Camera className="mr-2 h-4 w-4" />
                  Take Picture
                </Button>

                <Button 
                  onClick={handleGetLocation} 
                  disabled={!permissions.location}
                  className="w-full"
                  variant="outline"
                >
                  <MapPin className="mr-2 h-4 w-4" />
                  Get Location
                </Button>

                <Button 
                  onClick={handleShowNotification} 
                  disabled={!permissions.notifications}
                  className="w-full"
                  variant="outline"
                >
                  <Bell className="mr-2 h-4 w-4" />
                  Send Notification
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Permissions Status</CardTitle>
                <CardDescription>
                  Current permission states for device features
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Camera className="h-4 w-4" />
                      <span className="text-sm">Camera</span>
                    </div>
                    <Badge variant={permissions.camera ? 'default' : 'secondary'}>
                      {permissions.camera ? 'Granted' : 'Denied'}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4" />
                      <span className="text-sm">Location</span>
                    </div>
                    <Badge variant={permissions.location ? 'default' : 'secondary'}>
                      {permissions.location ? 'Granted' : 'Denied'}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Bell className="h-4 w-4" />
                      <span className="text-sm">Notifications</span>
                    </div>
                    <Badge variant={permissions.notifications ? 'default' : 'secondary'}>
                      {permissions.notifications ? 'Granted' : 'Denied'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pwa" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">PWA Status</CardTitle>
                <CardDescription>
                  Progressive Web App installation and update status
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Installable</span>
                  <Badge variant={isInstallable ? 'default' : 'secondary'}>
                    {isInstallable ? 'Yes' : 'No'}
                  </Badge>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm">Network Status</span>
                  <Badge variant={isOnline ? 'default' : 'destructive'}>
                    {isOnline ? 'Online' : 'Offline'}
                  </Badge>
                </div>

                {!isInstalled && isInstallable && (
                  <Button onClick={handleInstallApp} className="w-full">
                    <Download className="mr-2 h-4 w-4" />
                    Install PWA
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">App Capabilities</CardTitle>
                <CardDescription>
                  Features available in PWA mode
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <CloudOff className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Offline Functionality</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Bell className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Push Notifications</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Globe className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Background Sync</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Smartphone className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Native App Feel</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="offline" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Offline Actions</CardTitle>
                <CardDescription>
                  Test offline functionality and sync capabilities
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={handleOfflineAction} className="w-full">
                  <CloudOff className="mr-2 h-4 w-4" />
                  Queue Offline Action
                </Button>

                <Button onClick={handleSaveOfflineData} className="w-full" variant="outline">
                  <Battery className="mr-2 h-4 w-4" />
                  Save Data Offline
                </Button>

                <div className="text-sm text-muted-foreground">
                  <p>Pending actions: {Array.isArray(pendingActions) ? pendingActions.length : 0}</p>
                  <p>Network status: {storageOnline ? 'Connected' : 'Disconnected'}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Sync Status</CardTitle>
                <CardDescription>
                  Monitor offline data synchronization
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Pending Sync Items</span>
                    <Badge variant={Array.isArray(pendingActions) && pendingActions.length > 0 ? 'destructive' : 'default'}>
                      {Array.isArray(pendingActions) ? pendingActions.length : 0}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    {Array.isArray(pendingActions) && pendingActions.length > 0 && (
                      <>
                        <div className="text-xs text-muted-foreground">Recent pending actions:</div>
                        {pendingActions.slice(0, 3).map((action) => (
                          <div key={action.id} className="text-xs p-2 bg-muted rounded">
                            {action.type} - {action.endpoint}
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  <Progress 
                    value={Array.isArray(pendingActions) && pendingActions.length > 0 ? 25 : 100} 
                    className="h-2" 
                  />
                  
                  <p className="text-xs text-muted-foreground">
                    {Array.isArray(pendingActions) && pendingActions.length > 0 
                      ? 'Actions will sync when online' 
                      : 'All data synchronized'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};